import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';
import type { PetitionStatus } from '@change-corgi/middle-end-gql-schema';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { DecisionMakerResponsePageCombinedState, DecisionMakerResponsePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { DecisionMakerResponsePageDataContextProvider } from './pageData';
import { DecisionMakerResponsePageFcmContextProvider } from './pageFcm';
import { DecisionMakerResponsePageFcmExperimentsContextProvider } from './pageFcmExperiments';
import { DecisionMakerResponsePageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<DecisionMakerResponsePageCombinedState> };

type Props = {
	petitionSlugOrId: string;
	responseId: string;
	prefetchedData: DecisionMakerResponsePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function DecisionMakerResponsePageContextProvider({
	children,
	petitionSlugOrId,
	responseId,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(petitionSlugOrId, responseId, prefetchedData);
	const userId = useCurrentUserId();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageFcm, pageFcmExperiments } = pageContextState;

	return (
		<DecisionMakerResponsePageDataContextProvider pageData={pageData}>
			<DecisionMakerResponsePageFcmContextProvider pageFcm={pageFcm}>
				<DecisionMakerResponsePageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
					<DecisionMakerResponsePageStateContextProvider>
						<TrackingContext
							properties={{
								dm_response_id: `dm_response_${pageData.data.decisionMakerResponse.id}`,
								petition_id: `petition_${pageData.data.decisionMakerResponse.petition.id}`,
								promotable: pageData.data.decisionMakerResponse.petition.promotable,
								signed: pageData.data.petitionSigned,
								own_petition: pageData.data.decisionMakerResponse.petition.user.id === userId,
								petition_status: getTrackingPetitionStatus(pageData.data.decisionMakerResponse.petition.status),
							}}
						>
							{children}
						</TrackingContext>
					</DecisionMakerResponsePageStateContextProvider>
				</DecisionMakerResponsePageFcmExperimentsContextProvider>
			</DecisionMakerResponsePageFcmContextProvider>
		</DecisionMakerResponsePageDataContextProvider>
	);
}

function getTrackingPetitionStatus(status: PetitionStatus) {
	switch (status) {
		case 'VICTORY':
			return 'victory';
		case 'CLOSED':
			return 'closed';
		default:
			return 'active';
	}
}
